import React, { Component } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import equal from 'deep-equal'
import { smooth } from '@sainsburys-tech/boltui-utils'

import ErrorPage from '../components/ErrorPage/ErrorPage'

import { BrandAndVocabContextConsumer } from '../providers/BrandConfigContext/BrandConfigContext'

import { updateQuantity } from '../actionCreators/productStore/productStoreActionCreators'
import { fetchEssentialExtrasRequest } from '../actionCreators/essentialExtras/essentialExtrasActionCreators'
import { openAccordion } from '../helpers/openAccordion'
import { UniformTracking } from '../components/UniformTracking/UniformTracking'

import DefaultTemplate from '../templates/DefaultTemplate'

const bindAnchorLinksTosmoothing = () => {
  const links = [...document.querySelectorAll('a')]
  links.forEach(link => {
    const targetId = link.getAttribute('href') || ''
    if (!targetId.startsWith('#') || targetId === '#content') return
    link.addEventListener('click', event => {
      event.preventDefault()
      smooth.scrollHere(document.querySelector(targetId), 130, 500)
      return false
    })
  })
}

const anchor = (typeof window !== 'undefined' && window?.location?.hash) || null

class ProductPage extends Component {
  shouldComponentUpdate(nextProps) {
    return !equal(this.props, nextProps)
  }

  componentDidMount = () => {
    bindAnchorLinksTosmoothing()
    if (process.env.BRAND === 'TU') {
      this.props.requestEssentialExtras({
        partNumber: this.props.productId
      })
    }
    if (anchor) {
      setTimeout(() => {
        openAccordion(anchor)
      }, 1000)
    }
  }

  componentDidUpdate = prevProps => {
    const hasStoreChanged = prevProps?.storeLocator?.storeNumber !== this?.props?.storeLocator?.storeNumber
    const hasDeliveryPostcodeChanged =
      prevProps?.storeLocator?.deliveryPostcode !== this?.props?.storeLocator?.deliveryPostcode
    const hasProductIdChanged = prevProps?.productId !== this?.props?.productId
    const hasLocalisationFinished = prevProps?.storeLocator?.isFinished !== this.props?.storeLocator?.isFinished

    // Refetch essential extras if store, postcode, product or isFinished has changed and it is not a Tu product
    const hasLocalisationChanged =
      process.env.BRAND !== 'TU' && (hasStoreChanged || hasDeliveryPostcodeChanged || hasLocalisationFinished)

    if (hasProductIdChanged || hasLocalisationChanged) {
      this.props.requestEssentialExtras({
        partNumber: this.props.productId,
        postcode: this.props.storeLocator?.deliveryPostcode,
        storeId: this.props.storeLocator?.storeNumber || ''
      })
    }
  }

  render() {
    const { productId, productStore } = this.props
    const {
      meta: { errorCode, errorDetail }
    } = productStore

    const brand = productStore.data?.attributes ? productStore.data.attributes.brand : ''
    const isMadeToOrder = productStore.data?.attributes?.madeToOrder

    if (errorDetail || errorCode) {
      const errorPageProps = {
        failureStatus: errorCode || 404,
        parentPartNumber: productId
      }

      return <ErrorPage {...errorPageProps} />
    }

    const defaultOptions = { ...this.props, isMadeToOrder }

    return (
      <BrandAndVocabContextConsumer>
        <UniformTracking
          catId={defaultOptions?.breadcrumb[0]?.id}
          brand={brand}
          l2CatId={defaultOptions?.breadcrumb[1]?.id}
          l3CatId={defaultOptions?.breadcrumb[2]?.id}
          productId={productId}
          trolleyItemsAdded={this.props.trolleyItemsAdded}
        />
        <DefaultTemplate {...defaultOptions} />
      </BrandAndVocabContextConsumer>
    )
  }
}

const mapStateToProps = state => {
  const {
    productStore,
    storeLocator,
    bazaarVoiceReview,
    bazaarVoiceQuestionAnswer,
    alternatives,
    specialOffersCreator,
    essentialExtras,
    trolleyItems
  } = state

  const {
    data: { productId }
  } = productStore

  const {
    data: { productReviews = [], stats: reviewStatistics = {} }
  } = bazaarVoiceReview

  const {
    data: { [productId]: alternativesStore }
  } = alternatives

  const trolleyItemsAdded = trolleyItems?.added?.flat(Infinity) ?? []
  const props = {
    ...productStore.data,
    productStore,
    storeLocator: storeLocator.data,
    reviews: productReviews[productId],
    reviewStatistics,
    bazaarVoiceReviewStore: bazaarVoiceReview.data,
    bazaarVoiceQuestionAnswerStore: bazaarVoiceQuestionAnswer.data,
    alternativesStore,
    specialOffersCreator: specialOffersCreator.data,
    essentialExtras,
    trolleyItemsAdded
  }

  return props
}

ProductPage.propTypes = {
  attributes: PropTypes.object,
  badges: PropTypes.array,
  priorityMeta: PropTypes.object,
  ratingSummary: PropTypes.object,
  prices: PropTypes.object,
  alternatives: PropTypes.array,
  reviews: PropTypes.array,
  reviewStatistics: PropTypes.object,
  variants: PropTypes.object,
  breadcrumb: PropTypes.array,
  quantity: PropTypes.number,
  legalText: PropTypes.object,
  altView: PropTypes.bool,
  media: PropTypes.object,
  featuredProducts: PropTypes.array,
  featuredProductsLoading: PropTypes.bool,
  taxonomyCategory: PropTypes.object,
  templateType: PropTypes.string,
  creditPlans: PropTypes.object,
  comparison: PropTypes.array,
  warrantyProductDetails: PropTypes.object,
  productCare: PropTypes.object
}

const mapDispatchToProps = dispatch => {
  const props = {
    updateQuantity: payload => dispatch(updateQuantity(payload)),
    requestEssentialExtras: payload => dispatch(fetchEssentialExtrasRequest(payload))
  }
  return props
}

const reduxComponent = connect(mapStateToProps, mapDispatchToProps)(ProductPage)

export default reduxComponent
export { ProductPage, mapStateToProps }
